import {
    createContext,
    useContext,
    useMemo,
    ReactNode,
    ReactElement
} from 'react'
import { GraphQLError } from '@pylot-data/graphqlFetch'
import type { MutatorCallback } from 'swr'
import { useCart } from '@corratech/pylot-cart-manager/src/use-cart'
import { Cart } from '@pylot-data/pylotschema'

type CartData = {
    data: {
        cart: Cart
        isOptimistic?: boolean
    }
    errors?: GraphQLError[]
}

type MutateFn = (
    data?: CartData | Promise<CartData> | MutatorCallback<CartData>,
    shouldRevalidate?: boolean
) => Promise<CartData | undefined>

export interface PLPCartContextType {
    data: CartData | undefined
    mutate: MutateFn
}

export const PLPCartContext = createContext<PLPCartContextType>({
    data: undefined,
    mutate: () =>
        new Promise<CartData | undefined>((resolve) => resolve(undefined))
})

export const PLPCartProvider = ({
    children
}: {
    children: ReactNode
}): ReactElement => {
    const { data, mutate } = useCart()

    const value = useMemo(
        () => ({
            data,
            mutate
        }),
        [data, mutate]
    )

    return (
        <PLPCartContext.Provider value={value}>
            {children}
        </PLPCartContext.Provider>
    )
}

export const usePLPCartContext = (): PLPCartContextType => {
    const context = useContext(PLPCartContext)

    if (!context) {
        throw new Error(
            'usePLPCartContext must be used within a PLPCartProvider'
        )
    }

    return context
}
